import { CommonModule } from '@angular/common';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, forwardRef } from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormBuilder,
  FormGroup,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  ValidatorFn,
  Validators
} from '@angular/forms';
import { IonicModule } from '@ionic/angular';
import { TranslateModule } from '@ngx-translate/core';
import { Observable, Subject, Subscription, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, filter, finalize, map, switchMap } from 'rxjs/operators';
import { osService } from 'src/app/_services/osService.service';
import { getZipCodeValidatorByCountry } from '../../../helpers/utils';

interface AddressSuggestion {
  title: string | null;
  street: string | null;
  zip_code: string | null;
  city: string | null;
  country: string | null;
  position: { lat: number | null; long: number | null } | null;
}

interface OpenStreetResult {
  display_name?: string;
  lat?: string;
  lon?: string;
  address?: {
    house_number?: string;
    road?: string;
    postcode?: string;
    city?: string;
    town?: string;
    village?: string;
    municipality?: string;
    county?: string;
    country?: string;
  };
}

@Component({
  selector: 'app-progressive-address-fields',
  templateUrl: './progressive-address-fields.component.html',
  styleUrls: ['./progressive-address-fields.component.scss'],
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, IonicModule, TranslateModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ProgressiveAddressFieldsComponent),
      multi: true
    }
  ]
})
export class ProgressiveAddressFieldsComponent implements ControlValueAccessor, OnInit, OnChanges, OnDestroy {
  @Input() submitted = false;
  @Input() role = '';
  @Input() countryCode = 'FR';
  @Input() required = false;

  addressForm: FormGroup;

  showZipField = false;
  showCityField = false;

  addressSuggestions: AddressSuggestion[] = [];
  zipSuggestions: AddressSuggestion[] = [];
  citySuggestions: AddressSuggestion[] = [];
  loadingAddressSuggestions = false;
  loadingZipSuggestions = false;
  loadingCitySuggestions = false;
  showAddressDropdown = false;
  showZipDropdown = false;
  showCityDropdown = false;
  selectedStripeLocation: {
    line1: string;
    postalCode: string;
    city: string;
    country: string;
    coordinates: { lat: number | null; long: number | null } | null;
  } | null = null;

  private onChange = (value: any) => {};
  private onTouched = () => {};
  private searchAddress$ = new Subject<string>();
  private searchZip$ = new Subject<string>();
  private searchCity$ = new Subject<string>();
  private subscriptions = new Subscription();

  constructor(
    private http: HttpClient,
    private fb: FormBuilder,
    private osService: osService
  ) {
    this.addressForm = this.fb.group({
      address: ['', this.getAddressValidators()],
      city: ['', this.getCityValidators()],
      zip_code: ['', this.getZipValidators()]
    });

    this.subscriptions.add(
      this.addressForm.valueChanges.subscribe(value => {
        this.onChange(value);
      })
    );
  }

  ngOnInit(): void {
    this.setupAutocompleteStreams();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['required'] || changes['countryCode']) {
      this.updateValidators();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  writeValue(value: any): void {
    if (value) {
      this.addressForm.patchValue(value, { emitEvent: false });
      this.syncFieldVisibilityFromValues();
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.addressForm.disable();
      return;
    }
    this.addressForm.enable();
  }

  get addressControl() {
    return this.addressForm.get('address');
  }

  get cityControl() {
    return this.addressForm.get('city');
  }

  get zipControl() {
    return this.addressForm.get('zip_code');
  }

  updateRole(role: string): void {
    this.role = role;
    this.updateValidators();
  }

  updateCountry(countryCode: string): void {
    this.countryCode = countryCode;
    this.updateValidators();
  }

  onAddressInput(value: string): void {
    // Tant qu'aucune suggestion n'est selectionnee, ne pas afficher les champs suivants
    this.selectedStripeLocation = null;
    this.showZipField = false;
    this.showCityField = false;
    this.zipControl?.setValue('', { emitEvent: false });
    this.cityControl?.setValue('', { emitEvent: false });
    this.showAddressDropdown = true;
    this.searchAddress$.next(value || '');
  }

  onZipInput(value: string): void {
    this.showCityField = !!value?.trim();
    this.showZipDropdown = true;
    this.searchZip$.next(value || '');
  }

  onCityInput(value: string): void {
    this.showCityDropdown = true;
    this.searchCity$.next(value || '');
  }

  selectAddressSuggestion(item: AddressSuggestion): void {
    const mapped = this.mapStripeSuggestion(item);
    if (mapped.line1) {
      this.addressControl?.setValue(mapped.line1);
    }
    // Afficher les champs suivants uniquement apres selection d'une option
    this.showZipField = true;

    const detectedPostalCode = mapped.postalCode || this.extractPostalCodeFromText(item.title || '');

    if (detectedPostalCode) {
      this.zipControl?.setValue(detectedPostalCode);
    } else {
      // Fallback manuel si zip_code est null
      this.zipControl?.setValue('');
    }
    // Apres selection d'une adresse, afficher aussi la ville
    this.showCityField = true;
    if (mapped.city) {
      this.cityControl?.setValue(mapped.city);
    }
    this.selectedStripeLocation = mapped;
    this.showAddressDropdown = false;
  }

  selectZipSuggestion(item: AddressSuggestion): void {
    const mapped = this.mapStripeSuggestion(item);
    if (mapped.postalCode) {
      this.zipControl?.setValue(mapped.postalCode);
    }
    if (mapped.city && !this.cityControl?.value) {
      this.cityControl?.setValue(mapped.city);
    }
    this.selectedStripeLocation = mapped;
    this.showCityField = true;
    this.showZipDropdown = false;
  }

  selectCitySuggestion(item: AddressSuggestion): void {
    const mapped = this.mapStripeSuggestion(item);
    if (mapped.city) {
      this.cityControl?.setValue(mapped.city);
    }
    if (mapped.postalCode && !this.zipControl?.value) {
      this.zipControl?.setValue(mapped.postalCode);
    }
    this.selectedStripeLocation = mapped;
    this.showCityDropdown = false;
  }

  hideSuggestions(type: 'address' | 'zip' | 'city'): void {
    setTimeout(() => {
      if (type === 'address') this.showAddressDropdown = false;
      if (type === 'zip') this.showZipDropdown = false;
      if (type === 'city') this.showCityDropdown = false;
    }, 150);
  }

  openSuggestions(type: 'address' | 'zip' | 'city'): void {
    if (type === 'address' && this.addressSuggestions.length > 0) {
      this.showAddressDropdown = true;
    }
    if (type === 'zip' && this.zipSuggestions.length > 0) {
      this.showZipDropdown = true;
    }
    if (type === 'city' && this.citySuggestions.length > 0) {
      this.showCityDropdown = true;
    }
  }

  toggleSuggestions(type: 'address' | 'zip' | 'city', event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    if (type === 'address' && this.addressSuggestions.length > 0) {
      this.showAddressDropdown = !this.showAddressDropdown;
    }
    if (type === 'zip' && this.zipSuggestions.length > 0) {
      this.showZipDropdown = !this.showZipDropdown;
    }
    if (type === 'city' && this.citySuggestions.length > 0) {
      this.showCityDropdown = !this.showCityDropdown;
    }
  }

  onFieldBlur(fieldName: string): void {
    this.onTouched();
  }

  forceValidation(): void {
    this.addressControl?.markAsTouched();
    this.cityControl?.markAsTouched();
    this.zipControl?.markAsTouched();
    this.addressControl?.updateValueAndValidity();
    this.cityControl?.updateValueAndValidity();
    this.zipControl?.updateValueAndValidity();

    this.addressForm?.updateValueAndValidity();
  }

  private setupAutocompleteStreams(): void {
    this.subscriptions.add(
      this.searchAddress$
        .pipe(
          debounceTime(450),
          map(term => term.trim()),
          filter(term => term.length >= 3),
          distinctUntilChanged(),
          switchMap(term => {
            this.loadingAddressSuggestions = true;
            this.addressSuggestions = [];
            this.showAddressDropdown = false;
            return this.fetchSuggestions(term).pipe(
              finalize(() => {
                this.loadingAddressSuggestions = false;
              })
            );
          })
        )
        .subscribe(list => {
          this.addressSuggestions = list;
          this.showAddressDropdown = list.length > 0;
        })
    );

    this.subscriptions.add(
      this.searchZip$
        .pipe(
          debounceTime(450),
          map(term => term.trim()),
          filter(term => term.length >= 3),
          distinctUntilChanged(),
          switchMap(term => {
            this.loadingZipSuggestions = true;
            this.zipSuggestions = [];
            this.showZipDropdown = false;
            const address = this.addressControl?.value?.trim() || '';
            const query = `${address} ${term}`.trim();
            return this.fetchSuggestions(query).pipe(
              finalize(() => {
                this.loadingZipSuggestions = false;
              })
            );
          })
        )
        .subscribe(list => {
          this.zipSuggestions = list;
          this.showZipDropdown = list.length > 0;
        })
    );

    this.subscriptions.add(
      this.searchCity$
        .pipe(
          debounceTime(450),
          map(term => term.trim()),
          filter(term => term.length >= 3),
          distinctUntilChanged(),
          switchMap(term => {
            this.loadingCitySuggestions = true;
            this.citySuggestions = [];
            this.showCityDropdown = false;
            const address = this.addressControl?.value?.trim() || '';
            const zip = this.zipControl?.value?.trim() || '';
            const query = `${address} ${zip} ${term}`.trim();
            return this.fetchSuggestions(query).pipe(
              finalize(() => {
                this.loadingCitySuggestions = false;
              })
            );
          })
        )
        .subscribe(list => {
          this.citySuggestions = list;
          this.showCityDropdown = list.length > 0;
        })
    );
  }

  private fetchSuggestions(term: string) {
    if (!term || term.trim().length < 3) {
      return of([]);
    }

    return this.osService.autocompleteStripe(term.trim()).pipe(
      map((response: any): AddressSuggestion[] => {
        const payload = Array.isArray(response)
          ? response
          : Array.isArray(response?.data)
            ? response.data
            : [];
        return this.dedupeSuggestions(payload);
      }),
      switchMap((stripeSuggestions: AddressSuggestion[]) => {
        if (stripeSuggestions.length > 0) {
          return of(stripeSuggestions);
        }
        return this.fetchOpenStreetSuggestions(term.trim());
      }),
      catchError(() => this.fetchOpenStreetSuggestions(term.trim()))
    );
  }

  private fetchOpenStreetSuggestions(term: string): Observable<AddressSuggestion[]> {
    const params = new HttpParams()
      .set('q', term)
      .set('format', 'jsonv2')
      .set('addressdetails', '1')
      .set('limit', '8')
      .set('accept-language', 'fr')
      .set('countrycodes', (this.countryCode || 'fr').toLowerCase());

    return this.http
      .get<OpenStreetResult[]>('https://nominatim.openstreetmap.org/search', { params })
      .pipe(
        map((results: OpenStreetResult[] = []) => {
          const mapped = results.map((item) => this.mapOpenStreetSuggestion(item));
          return this.dedupeSuggestions(mapped).filter((item) => !!item.title || !!item.street);
        }),
        catchError(() => of([]))
      );
  }

  private mapOpenStreetSuggestion(item: OpenStreetResult): AddressSuggestion {
    const address = item?.address || {};
    const streetParts = [address.house_number, address.road].filter(Boolean);
    const street = streetParts.join(' ').trim() || null;
    const city =
      address.city ||
      address.town ||
      address.village ||
      address.municipality ||
      address.county ||
      null;

    return {
      title: item?.display_name || street || city || null,
      street,
      zip_code: address.postcode || null,
      city,
      country: address.country || null,
      position: {
        lat: item?.lat ? Number(item.lat) : null,
        long: item?.lon ? Number(item.lon) : null
      }
    };
  }

  suggestionSecondary(item: AddressSuggestion): string {
    const parts = [item.street, item.city, item.country]
      .map((value) => (value || '').trim())
      .filter((value) => !!value);
    return parts.join(' - ');
  }

  suggestionTitle(item: AddressSuggestion): string {
    const base = (item.title || item.street || '').trim();
    const zip = (item.zip_code || '').trim();
    return zip ? `${base} (${zip})` : base;
  }

  trackBySuggestion = (_: number, item: AddressSuggestion): string => {
    return this.buildSuggestionKey(item);
  };

  private mapStripeSuggestion(item: AddressSuggestion): {
    line1: string;
    postalCode: string;
    city: string;
    country: string;
    coordinates: { lat: number | null; long: number | null } | null;
  } {
    return {
      line1: item?.street || item?.title || '',
      postalCode: item?.zip_code || '',
      city: item?.city || '',
      country: item?.country || '',
      coordinates: item?.position || null
    };
  }

  private extractPostalCodeFromText(text: string): string {
    const match = (text || '').match(/\b\d{4,6}\b/);
    return match ? match[0] : '';
  }

  private dedupeSuggestions(items: AddressSuggestion[]): AddressSuggestion[] {
    const unique = new Map<string, AddressSuggestion>();
    items.forEach((item) => {
      const key = this.buildSuggestionKey(item);
      if (!unique.has(key)) {
        unique.set(key, item);
      }
    });
    return Array.from(unique.values());
  }

  private buildSuggestionKey(item: AddressSuggestion): string {
    return [
      (item.title || '').trim().toLowerCase(),
      (item.street || '').trim().toLowerCase(),
      (item.zip_code || '').trim().toLowerCase(),
      (item.city || '').trim().toLowerCase(),
      (item.country || '').trim().toLowerCase()
    ].join('|');
  }

  private syncFieldVisibilityFromValues(): void {
    this.showZipField = !!this.addressControl?.value?.trim();
    this.showCityField = !!this.zipControl?.value?.trim();
  }

  private getAddressValidators() {
    const validators = [];
    if (this.required) {
      validators.push(Validators.required);
    }
    validators.push(this.addressValidator());
    return validators;
  }

  private getCityValidators() {
    const validators = [];
    if (this.required) {
      validators.push(Validators.required);
    }
    validators.push(this.cityValidator());
    return validators;
  }

  private getZipValidators() {
    const validators = [];
    if (this.required) {
      validators.push(Validators.required);
    }
    validators.push(this.zipValidator());
    return validators;
  }

  private updateValidators(): void {
    const addressControl = this.addressControl;
    const cityControl = this.cityControl;
    const zipControl = this.zipControl;

    addressControl?.setValidators(this.getAddressValidators());
    cityControl?.setValidators(this.getCityValidators());
    zipControl?.setValidators(this.getZipValidators());

    addressControl?.updateValueAndValidity();
    cityControl?.updateValueAndValidity();
    zipControl?.updateValueAndValidity();
  }

  private addressValidator(): ValidatorFn {
    return (control: AbstractControl) => {
      const value = control.value;
      if (!value) return null;

      const trimmedValue = value.trim();
      if (trimmedValue.length < 3) return { invalidAddress: true };

      const addressRegex = /^[\p{L}\p{M}0-9\s\-.,#/()'’]+$/u;
      if (!addressRegex.test(trimmedValue)) return { invalidAddress: true };
      if (!/[\p{L}\p{M}]/u.test(trimmedValue)) return { invalidAddress: true };

      return null;
    };
  }

  private cityValidator(): ValidatorFn {
    return (control: AbstractControl) => {
      const value = control.value;
      if (!value) return null;

      const trimmedValue = value.trim();
      if (trimmedValue.length < 2) return { invalidCity: true };

      const cityRegex = /^[\p{L}\p{M}\s\-'.’]+$/u;
      if (!cityRegex.test(trimmedValue)) return { invalidCity: true };
      if (!/[\p{L}\p{M}]/u.test(trimmedValue)) return { invalidCity: true };

      return null;
    };
  }

  private zipValidator(): ValidatorFn {
    return (control: AbstractControl) => {
      const value = control.value;
      if (!value) return null;

      const formatValidator = getZipCodeValidatorByCountry(() => this.countryCode);
      return formatValidator(control);
    };
  }

  shouldShowError(control: AbstractControl | null): boolean {
    if (!control) {
      return false;
    }
    return this.submitted || control.touched || control.dirty;
  }

  hasError(control: AbstractControl | null, errorKey: string): boolean {
    if (!control) {
      return false;
    }
    return this.shouldShowError(control) && control.hasError(errorKey);
  }
}
